import PropTypes from 'prop-types';

import { BackendState, BackendStateWithData } from '../../types';
import { CardDetailsDTO } from '../../types/dto/CardDetailsDTO';
import { CustomerDataDTO } from '../../types/dto/CustomerDataDTO';
import { InvoiceGroupDTO } from '../../types/dto/InvoiceGroupDTO';
import { MaintenanceContractChartResultDTO } from '../../types/dto/MaintenanceContractChartResultDTO';
import { MaintenanceContractUpgradeChartResultDTO } from '../../types/dto/MaintenanceContractUpgradeChartResultDTO';
import { SRPMMChartResultDTO } from '../../types/dto/SRPMMChartResultDTO';
import { VehicleDataDTO } from '../../types/dto/VehicleDataDTO';
import { VehicleHistoryDTO } from '../../types/dto/VehicleHistoryDTO';

//////////////////////////////////////////////////////////////////////////////////////////
// Misc shapes
//////////////////////////////////////////////////////////////////////////////////////////
export interface CustomerErrorInfo {
    response: any;
    errorCode: string;
    errorMessage: string | null;
    exceptionType: string | null;
}

export class CustomerError extends Error {
    public customerErrorInfo: CustomerErrorInfo;

    constructor(customerErrorInfo: CustomerErrorInfo, ...params: any[]) {
        // Pass remaining arguments (including vendor specific ones) to parent constructor
        super(...params);

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, CustomerError);
        }

        if (customerErrorInfo.errorMessage !== null) {
            this.message = customerErrorInfo.errorMessage;
        }
        // Custom debugging information
        this.customerErrorInfo = customerErrorInfo;
    }
}

export interface CustomerData {
    readonly customerData?: CustomerDataDTO;
}

export interface VehicleData {
    readonly vehicleData?: VehicleDataDTO;
}

export interface CustomerCards {
    readonly cards: CardDetailsDTO[];
}

export interface VehicleInvoices {
    readonly invoices: InvoiceGroupDTO[];
}

export interface VehicleMaintenanceContracts {
    readonly maintenanceContracts: MaintenanceContractChartResultDTO;
}

export interface VehicleMaintenanceContractUpgrades {
    readonly maintenanceContractUpgrades: MaintenanceContractUpgradeChartResultDTO;
}

export interface VehicleMaintenanceContractPDF {
    readonly maintenanceContractPDF?: Blob;
}

export interface VehicleSRPMM {
    readonly srpmms: SRPMMChartResultDTO;
}

export interface VehicleAllVisits {
    readonly visits: VehicleHistoryDTO;
}

export interface CustomerDataState extends BackendStateWithData<CustomerData> { }
export interface VehicleDataState extends BackendStateWithData<VehicleData> { }
export interface CustomerCardsState extends BackendStateWithData<CardDetailsDTO[]> { }
export interface VehicleInvoicesState extends BackendStateWithData<InvoiceGroupDTO[]> { }
export interface VehicleMaintenanceContractsState extends BackendStateWithData<MaintenanceContractChartResultDTO> { }
export interface VehicleMaintenanceContractUpgradesState extends BackendStateWithData<MaintenanceContractUpgradeChartResultDTO> { }
export interface VehicleMaintenanceContractPDFState extends BackendStateWithData<Blob | undefined> { }
export interface VehicleSRPMMState extends BackendStateWithData<SRPMMChartResultDTO> { }
export interface VehicleAllVisitsState extends BackendStateWithData<VehicleHistoryDTO> { }
export interface ChangePasswordState extends BackendState { }
// export interface DeleteAccountState extends BackendState { }
export interface UpdateCustomerState extends BackendState { }
export interface UpdateCustomerForSubscriptionState extends BackendState { }
export interface UpdateNifState extends BackendState { }
export interface AddVehicleState extends BackendState { }
export interface DeleteVehicleState extends BackendState { }
export interface DeleteCardState extends BackendState { }
export interface UploadProfilePictureState extends BackendState { }
export interface UploadVehicleDocsState extends BackendState { }
export interface AddValueAndCareCardState extends BackendState { }

export interface CustomerState {
    readonly changePassword: ChangePasswordState;
    // readonly deleteAccount: DeleteAccountState;
    readonly updateCustomer: UpdateCustomerState;
    readonly updateCustomerForSubscription: UpdateCustomerForSubscriptionState;
    readonly updateNif: UpdateNifState;
    readonly addVehicle: AddVehicleState;
    readonly deleteVehicle: DeleteVehicleState;
    readonly deleteCard: DeleteCardState;
    readonly uploadProfilePicture: UploadProfilePictureState;
    readonly uploadVehicleDocs: UploadVehicleDocsState;
    readonly addValueAndCareCard: AddValueAndCareCardState;
    readonly customerData: CustomerDataState;
    readonly vehicleData: VehicleDataState;
    readonly customerCards: CustomerCardsState;
    readonly vehicleInvoices: VehicleInvoicesState;
    readonly vehicleMaintenanceContracts: VehicleMaintenanceContractsState;
    readonly vehicleMaintenanceContractUpgrades: VehicleMaintenanceContractUpgradesState;
    readonly vehicleMaintenanceContractPDF: VehicleMaintenanceContractPDFState;
    readonly vehicleSRPMM: VehicleSRPMMState;
    readonly vehicleAllVisits: VehicleAllVisitsState;
}

//////////////////////////////////////////////////////////////////////////////////////////
// Action shapes
//////////////////////////////////////////////////////////////////////////////////////////

export const CustomerActionPropTypes = PropTypes.shape({
    customerDataRequest: PropTypes.func.isRequired,
    customerDataSuccess: PropTypes.func.isRequired,
    customerDataFailure: PropTypes.func.isRequired,

    vehicleDataRequest: PropTypes.func.isRequired,
    vehicleDataSuccess: PropTypes.func.isRequired,
    vehicleDataFailure: PropTypes.func.isRequired,

    customerCardsRequest: PropTypes.func.isRequired,
    customerCardsSuccess: PropTypes.func.isRequired,
    customerCardsFailure: PropTypes.func.isRequired,

    changePasswordRequest: PropTypes.func.isRequired,
    changePasswordSuccess: PropTypes.func.isRequired,
    changePasswordFailure: PropTypes.func.isRequired,

    // deleteAccountRequest: PropTypes.func.isRequired,
    // deleteAccountSuccess: PropTypes.func.isRequired,
    // deleteAccountFailure: PropTypes.func.isRequired,

    updateCustomerRequest: PropTypes.func.isRequired,
    updateCustomerSuccess: PropTypes.func.isRequired,
    updateCustomerFailure: PropTypes.func.isRequired,

    updateCustomerForSubscriptionRequest: PropTypes.func.isRequired,
    updateCustomerForSubscriptionSuccess: PropTypes.func.isRequired,
    updateCustomerForSubscriptionFailure: PropTypes.func.isRequired,

    updateNifRequest: PropTypes.func.isRequired,
    updateNifSuccess: PropTypes.func.isRequired,
    updateNifFailure: PropTypes.func.isRequired,

    addVehicleRequest: PropTypes.func.isRequired,
    addVehicleSuccess: PropTypes.func.isRequired,
    addVehicleFailure: PropTypes.func.isRequired,

    deleteVehicleRequest: PropTypes.func.isRequired,
    deleteVehicleSuccess: PropTypes.func.isRequired,
    deleteVehicleFailure: PropTypes.func.isRequired,

    deleteCardRequest: PropTypes.func.isRequired,
    deleteCardSuccess: PropTypes.func.isRequired,
    deleteCardFailure: PropTypes.func.isRequired,

    uploadProfilePictureRequest: PropTypes.func.isRequired,
    uploadProfilePictureSuccess: PropTypes.func.isRequired,
    uploadProfilePictureFailure: PropTypes.func.isRequired,

    uploadVehicleDocsRequest: PropTypes.func.isRequired,
    uploadVehicleDocsSuccess: PropTypes.func.isRequired,
    uploadVehicleDocsFailure: PropTypes.func.isRequired,

    addValueAndCareCardRequest: PropTypes.func.isRequired,
    addValueAndCareCardSuccess: PropTypes.func.isRequired,
    addValueAndCareCardFailure: PropTypes.func.isRequired,

    vehicleInvoicesRequest: PropTypes.func.isRequired,
    vehicleInvoicesSuccess: PropTypes.func.isRequired,
    vehicleInvoicesFailure: PropTypes.func.isRequired,

    vehicleMaintenanceContractsRequest: PropTypes.func.isRequired,
    vehicleMaintenanceContractsSuccess: PropTypes.func.isRequired,
    vehicleMaintenanceContractsFailure: PropTypes.func.isRequired,

    vehicleMaintenanceContractUpgradesRequest: PropTypes.func.isRequired,
    vehicleMaintenanceContractUpgradesSuccess: PropTypes.func.isRequired,
    vehicleMaintenanceContractUpgradesFailure: PropTypes.func.isRequired,

    vehicleMaintenanceContractPDFRequest: PropTypes.func.isRequired,
    vehicleMaintenanceContractPDFSuccess: PropTypes.func.isRequired,
    vehicleMaintenanceContractPDFFailure: PropTypes.func.isRequired,

    vehicleSRPMMRequest: PropTypes.func.isRequired,
    vehicleSRPMMSuccess: PropTypes.func.isRequired,
    vehicleSRPMMFailure: PropTypes.func.isRequired,

    vehicleAllVisitsRequest: PropTypes.func.isRequired,
    vehicleAllVisitsSuccess: PropTypes.func.isRequired,
    vehicleAllVisitsFailure: PropTypes.func.isRequired,
});
